/**
 * @file @ 模式下拉：mentionQuery debounce + 服务端 keyword 搜索
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { useKnowledgeBaseList, type IKnowledgeBaseItem } from './useKnowledgeBaseApi';
import { filterKnowledgeBasesWithDocs, KB_PANEL_SEARCH_DEBOUNCE_MS } from '../utils/knowledgeBaseUtils';

interface IUseKbAtDropdownListResult {
  atKbList: IKnowledgeBaseItem[];
  atListLoading: boolean;
  atListError: boolean;
  isAtSearchPending: boolean;
  debouncedAtQuery: string;
  fullKbList: IKnowledgeBaseItem[];
}

/**
 * @ 模式知识库列表：全量常驻 + mentionQuery 服务端搜索（双实例）
 * @param isAtMode - 是否 @ 模式
 * @param mentionQuery - @ 后输入的查询词
 */
export const useKbAtDropdownList = (
  isAtMode: boolean,
  mentionQuery: string,
): IUseKbAtDropdownListResult => {
  const [debouncedAtQuery, setDebouncedAtQuery] = useState('');
  const stableAtKbListRef = useRef<IKnowledgeBaseItem[]>([]);

  const { run: notifyAtSearchChange, cancel: cancelAtSearchChange } = useDebounceFn(
    (keyword: string) => {
      setDebouncedAtQuery(keyword.trim());
    },
    { wait: KB_PANEL_SEARCH_DEBOUNCE_MS },
  );

  useEffect(() => {
    if (!isAtMode) {
      return;
    }
    notifyAtSearchChange(mentionQuery);
  }, [isAtMode, mentionQuery, notifyAtSearchChange]);

  useEffect(() => {
    if (isAtMode) {
      return;
    }
    cancelAtSearchChange();
    setDebouncedAtQuery('');
  }, [isAtMode, cancelAtSearchChange]);

  const trimmedMentionQuery = mentionQuery.trim();
  const isAtSearchSynced = trimmedMentionQuery === debouncedAtQuery;
  const isAtSearchPending = isAtMode && !isAtSearchSynced;
  const isAtSearchActive = isAtMode && Boolean(debouncedAtQuery);

  const fullKbListResult = useKnowledgeBaseList();
  const searchKbListResult = useKnowledgeBaseList({
    keyword: debouncedAtQuery,
    ready: isAtSearchActive,
  });

  useEffect(() => {
    if (!isAtMode || isAtSearchPending) {
      return;
    }
    const nextList = isAtSearchActive
      ? searchKbListResult.kbList
      : fullKbListResult.kbList;
    stableAtKbListRef.current = filterKnowledgeBasesWithDocs(nextList);
  }, [
    isAtMode,
    isAtSearchPending,
    isAtSearchActive,
    searchKbListResult.kbList,
    fullKbListResult.kbList,
  ]);

  const atKbList = useMemo(() => {
    if (!isAtMode) {
      return [];
    }
    if (isAtSearchPending) {
      if (stableAtKbListRef.current.length) {
        return stableAtKbListRef.current;
      }
      return filterKnowledgeBasesWithDocs(fullKbListResult.kbList);
    }
    const source = isAtSearchActive
      ? searchKbListResult.kbList
      : fullKbListResult.kbList;
    return filterKnowledgeBasesWithDocs(source);
  }, [
    isAtMode,
    isAtSearchPending,
    isAtSearchActive,
    searchKbListResult.kbList,
    fullKbListResult.kbList,
  ]);

  const atListLoading = isAtMode && atKbList.length === 0 && (
    fullKbListResult.listLoading
    || isAtSearchPending
    || (isAtSearchActive && searchKbListResult.listLoading)
  );

  const atListError = Boolean(isAtMode && (
    isAtSearchActive && isAtSearchSynced
      ? searchKbListResult.error
      : fullKbListResult.error
  ));

  return {
    atKbList,
    atListLoading,
    atListError,
    isAtSearchPending,
    debouncedAtQuery,
    fullKbList: fullKbListResult.kbList,
  };
};
